import React, { useState, useEffect } from "react";
import { Menu, X, ShoppingCart, Phone, Mail, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../shared/CartContext";
import CartDrawer from "./CartDrawer";
import "../index.css";
import Logo from "../components/logo";

export default function Header() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [storeOpen, setStoreOpen] = useState(false);
  const { totals } = useCart();
  const [copied, setCopied] = useState("");

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleCopy = async (text, type) => {
    try {
      // try modern clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // fallback
        const input = document.createElement("input");
        input.value = text;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopied(type);
      setTimeout(() => setCopied(""), 1500);
    } catch (err) {
      console.error("Copy failed:", err);
      alert("Unable to copy on this device.");
    }
  };

  // Unified cart button for both desktop and mobile
  const CartButton = () => (
    <button
      onClick={() => setCartOpen(true)}
      className="relative p-2.5 rounded-lg border border-gray-200 bg-white shadow-sm active:scale-95 transition focus:outline-none focus:ring-2 focus:ring-blue-500/30"
      aria-label="Open cart"
      title="Open cart"
    >
      <ShoppingCart className="h-6 w-6 text-gray-700" />
      {totals.count > 0 && (
        <span className="absolute -top-1 -right-1 text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5">
          {totals.count}
        </span>
      )}
    </button>
  );

  return (
    <header className="w-full shadow-md sticky top-0 z-50">
      {/* 🔔 Announcement Bar */}
      <section className="w-full bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-sm py-2 border-b border-gray-300">
        <div className="container mx-auto px-4 flex flex-row justify-between items-center flex-wrap">
          {/* Left - Phone */}
          <ul className="flex items-center gap-4">
            <li
              className="flex items-center gap-2 cursor-pointer group select-none"
              onClick={(e) => {
                e.preventDefault();
                handleCopy("+923214994888", "phone");
              }}
            >
              <Phone
                size={16}
                className="text-gray-600 group-hover:text-blue-600 transition-colors"
              />
              <span className="group-hover:text-blue-600 transition-colors">
                +92 321 4994888
              </span>
              {copied === "phone" && (
                <span className="ml-2 text-xs text-green-600 font-medium">
                  Copied!
                </span>
              )}
            </li>
          </ul>

          {/* Right - Email */}
          <ul className="flex items-center gap-4">
            <li
              className="flex items-center gap-2 cursor-pointer group select-none"
              onClick={(e) => {
                e.preventDefault();
                handleCopy("info@synergysoft.com.pk", "email");
              }}
            >
              <Mail
                size={16}
                className="text-gray-600 group-hover:text-blue-600 transition-colors"
              />
              <span className="group-hover:text-blue-600 transition-colors">
                info@synergysoft.com.pk
              </span>
              {copied === "email" && (
                <span className="ml-2 text-xs text-green-600 font-medium">
                  Copied!
                </span>
              )}
            </li>
          </ul>
        </div>
      </section>

      {/* 🧭 Navbar */}
      <nav className="bg-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <Logo size={60} />
          </Link>

          {/* Center Menu */}
          <ul className="hidden md:flex items-center gap-10 text-gray-700 font-medium">
            <li>
              <Link to="/" className="hover:text-blue-600 transition">
                Home
              </Link>
            </li>

            {/* Store Dropdown */}
            <li
              className="relative"
              onMouseEnter={() => setStoreOpen(true)}
              onMouseLeave={() => setStoreOpen(false)}
            >
              <button className="flex items-center gap-1 hover:text-blue-600 transition">
                Store <ChevronDown size={16} />
              </button>
              <AnimatePresence>
                {storeOpen && (
                  <motion.ul
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 mt-3 w-56 bg-white shadow-lg rounded-lg overflow-hidden z-50 border border-gray-100"
                  >
                    <li>
                      <Link
                        to="/hosting"
                        className="block px-6 py-3 hover:bg-gray-100 text-gray-700 whitespace-nowrap"
                      >
                        Hosting
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/domains"
                        className="block px-6 py-3 hover:bg-gray-100 text-gray-700 whitespace-nowrap"
                      >
                        Buy a New Domain
                      </Link>
                    </li>
                    {/* <li>
                      <Link
                        to="/transfer"
                        className="block px-6 py-3 hover:bg-gray-100 text-gray-700 whitespace-nowrap"
                      >
                        Transfer Domain to Us
                      </Link>
                    </li> */}
                    <li>
                      <Link
                        to="/other-services"
                        className="block px-6 py-3 hover:bg-gray-100 text-gray-700 whitespace-nowrap"
                      >
                        Other Services
                      </Link>
                    </li>
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>

            <li>
              <Link to="/FAQ" className="hover:text-blue-600 transition">
                FAQ
              </Link>
            </li>
            <li>
              <Link to="/about" className="hover:text-blue-600 transition">
                About Us
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-blue-600 transition">
                Contact
              </Link>
            </li>
          </ul>

          {/* Right Side - Unified Cart */}
          <div className="hidden md:flex items-center gap-5 relative">
            <CartButton />
          </div>

          {/* Mobile - Cart + Menu */}
          <div className="md:hidden flex items-center gap-2">
            <CartButton />
            <button
              className="text-gray-700 p-2.5 rounded-lg border border-gray-200 bg-white shadow-sm active:scale-95 transition focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              title={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </nav>

      {/* 📱 Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="md:hidden bg-white border-t border-gray-200 px-4 py-3 space-y-1 rounded-b-xl shadow-lg"
          >
            <Link
              to="/"
              className="block text-gray-800 hover:text-blue-600 py-3"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>

            <details className="group rounded-lg border border-gray-200 bg-gray-50">
              <summary className="cursor-pointer text-gray-700 hover:text-blue-600 flex items-center justify-between px-3 py-3 rounded-lg">
                Store
                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="pl-3 pb-3 space-y-1 text-gray-600">
                <Link
                  to="/hosting"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 hover:text-blue-600 text-gray-700 whitespace-nowrap"
                >
                  Hosting
                </Link>
                <Link
                  to="/domains"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 hover:text-blue-600 text-gray-700 whitespace-nowrap"
                >
                  Buy a New Domain
                </Link>
                {/* <Link
                  to="/transfer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 hover:text-blue-600 text-gray-700 whitespace-nowrap"
                >
                  Transfer Domain to Us
                </Link> */}
                <Link
                  to="/other-services"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 hover:text-blue-600 text-gray-700 whitespace-nowrap"
                >
                  Other Services
                </Link>
              </div>
            </details>

            <Link
              to="/FAQ"
              className="block py-3 text-gray-800 hover:text-blue-600 transition"
            >
              FAQ
            </Link>

            <Link
              to="/about"
              className="block py-3 text-gray-800 hover:text-blue-600 transition"
            >
              About Us
            </Link>

            <Link
              to="/contact"
              className="block text-gray-800 hover:text-blue-600 py-3"
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact Us
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </header>
  );
}
